import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import type { IRegistrationView } from '../types';
import { revisionLabel, unplacedFailure, validationErrors } from './registration-copy';
import { QuietAction, StepHeading, useStepAction } from './step-parts';
import {
  GivenAnswers,
  type IRouteAnswer,
  TeamRoutingQuestion,
  answerRefusal
} from './team-routing-question';

/**
 * `route` — a new team's gating questions, one at a time (survey-api's teams plan R16, R18).
 *
 * **survey-api keeps no answer between calls**, so the answers given so far are the page's,
 * for the life of the step, and every request carries all of them: the next question, an
 * outcome (`complete`) or the RIA firm survey (`redirected`) is what the server makes of the
 * whole set, each time. "Change my last answer" posts the set without its last answer, which
 * the server answers with the question that answer was to. A reload, a resumed tab or a step
 * back to the number starts from the first question again — the page holds the answers in
 * memory and nowhere else — and the step says so rather than pretending to remember.
 *
 * **The firm line is the view's `firmName`** (SR-416's `RegistrationOutcome`): the register's
 * label where exactly one register firm maps the lead's firms, else FINRA's name of the first
 * current firm, else nothing — and nothing is drawn then. Which question comes first is the
 * tree's, by that firm (the wirehouse list, another firm, none); no question asks which firm.
 *
 * A refusal of an answer (`errors['answers.<code>']`) is said on the question it names, and
 * nothing is re-read: the run is exactly where it was.
 */
export function RouteStep({
  view,
  given,
  onRoute,
  onRevise
}: {
  view: IRegistrationView;
  given: readonly IRouteAnswer[];
  onRoute: (given: readonly IRouteAnswer[]) => Promise<void>;
  onRevise: () => Promise<void>;
}) {
  const { pending, error, run } = useStepAction();
  const [retaken, setRetaken] = React.useState<Nullable<IRouteAnswer>>(null);
  const question = view.question;
  if (!question) throw new Error('The answer to the route step held no question');

  const key = `answers.${question.code}`;
  const messages = validationErrors(error)[key]?.map(answerRefusal);
  const failure = unplacedFailure(error, new Set([key]), 'Could not record your answer.');
  const firm = firmLine(view);

  const takeBack = () => {
    const last = given[given.length - 1];
    setRetaken(last);
    void run(() => onRoute(given.slice(0, -1)));
  };

  return (
    <>
      <StepHeading
        title="A few questions about your team"
        lede="Your answers decide which survey your team takes."
      />

      {firm && (
        <p
          className={cn(
            'mb-5 rounded-md border border-border p-3 text-sm',
            'bg-[var(--color-slight-blue)]/30'
          )}
        >
          {firm}
        </p>
      )}

      <GivenAnswers given={given} />

      <TeamRoutingQuestion
        // Keyed by the question and how far in it is, so a new question starts blank and a
        // taken-back one starts from the answer it had.
        key={`${question.code}|${given.length}`}
        question={question}
        initial={retaken?.question.code === question.code ? retaken.value : undefined}
        messages={messages}
        pending={pending}
        onAnswer={(value) => void run(() => onRoute([...given, { question, value }]))}
      />

      {failure && (
        <p role="alert" className="mt-4 text-sm font-medium text-destructive">
          {failure}
        </p>
      )}

      <p className="mt-5 text-xs text-muted-foreground">
        Your answers are kept on this page only — if it is reloaded, the questions start again
        from the first.
      </p>

      <div className="grid">
        {given.length > 0 && (
          <QuietAction label="Change my last answer" disabled={pending} onClick={takeBack} />
        )}
        <QuietAction
          label={revisionLabel(view)}
          disabled={pending}
          onClick={() => void run(onRevise)}
        />
      </div>
    </>
  );
}

/** The firm the route rests on, as the view names it — or null where it names none. */
function firmLine(view: IRegistrationView): Nullable<string> {
  return view.firmName ? `Your lead advisor’s firm: ${view.firmName}.` : null;
}
